import React, { useCallback, useMemo } from 'react';

import { Tooltip, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import AddCircleOutlinedIcon from '@material-ui/icons/AddCircleOutlined';
import { isNil } from 'lodash';
import { useQuery } from 'react-query';

import exercisesSetApi from 'api/rest/exercisesSets/exercisesSet';
import { ExerciseInSetDetails, ExerciseInSetDto } from 'api/rest/exercisesSets/exercisesSet.types';
import ButtonWithPopover, { ButtonWithPopoverElement } from 'components/ButtonWithPopover/ButtonWithPopover';
import { useGlobalDialogContext } from 'context/GlobalDialogContext';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';

import useStyles from './ChoosenExercisesButton.styles';
import useAddToExerciseSet from './ChosenExercisesButton.logic';

type Props = {
  disabled?: boolean;
  exerciseToAdd: Partial<ExerciseInSetDetails>;
};

const ChosenExercisesButton: React.FC<Props> = ({ exerciseToAdd, disabled }) => {
  // TODO - exercisesSet update events
  const { logEvent } = useUserLogContext();

  const plumbedExerciseToAdd: ExerciseInSetDto | null = useMemo(() => {
    if (!exerciseToAdd?.uuid || isNil(exerciseToAdd?.subject) || isNil(exerciseToAdd?.index)) return null;
    return {
      subject: exerciseToAdd.subject,
      index: exerciseToAdd.index,
      uuid: exerciseToAdd.uuid,
    };
  }, [exerciseToAdd]);

  const { GLOBAL_DIALOG, addToGlobalDialogQueue } = useGlobalDialogContext();

  const { data: exercisesSets } = useQuery(
    exercisesSetApi.getAll.generateQueryKey(),
    exercisesSetApi.getAll.request(),
    exercisesSetApi.getAll.requestBaseSettings,
  );

  const { addToExercisesSet } = useAddToExerciseSet();

  const openCreateNewExercisesSetModal = useCallback(() => {
    if (!plumbedExerciseToAdd) return;
    const existingSets = exercisesSets ? exercisesSets.map(({ name }) => name) : [];
    addToGlobalDialogQueue({ type: GLOBAL_DIALOG.CREATE_NEW_EXERCISES_SET, props: { existingSets, exerciseToAdd: plumbedExerciseToAdd } });
  }, [exerciseToAdd, plumbedExerciseToAdd, exercisesSets]);

  const styles = useStyles({ disabled });

  const elements = useMemo(() => {
    const result: ButtonWithPopoverElement[] = [];
    if (exercisesSets && plumbedExerciseToAdd) {
      exercisesSets.forEach(({ name, id: setId }) => {
        result.push({
          label: (
            <span>
              <span>{name}</span>
            </span>
          ),
          action: addToExercisesSet(setId, plumbedExerciseToAdd),
          key: setId,
        });
      });
    }
    result.push({
      label: (
        <span className={styles.addNewSetElement}>
          <AddCircleOutlinedIcon className={styles.addNewSetElementIcon} fontSize='small' />
          <span>Stwórz nowy zestaw</span>
        </span>
      ),
      action: openCreateNewExercisesSetModal,
      key: 'add new',
    });
    return result;
  }, [exercisesSets, exerciseToAdd, plumbedExerciseToAdd]);

  const buttonDisabled = disabled || !plumbedExerciseToAdd;

  return (
    <Tooltip title='Dodaj zadanie do zestawu zadań'>
      <ButtonWithPopover
        dense
        elements={elements}
        id={`Dodaj zadanie numer: ${exerciseToAdd?.index}`}
        buttonProps={{ className: styles.wrapper, disabled: buttonDisabled, size: 'small' }}
        popoverMinWidth={240}
      >
        <>
          <Typography className={styles.label}>{exerciseToAdd?.index}</Typography>
          <AddIcon className={styles.icon} fontSize='small' />
        </>
      </ButtonWithPopover>
    </Tooltip>
  );
};

export default ChosenExercisesButton;
